import { IconButton, LuIcon, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import type { Form, FormSubmission, Stored } from "@overmap-ai/common"
import { useNavigate } from "@tanstack/react-router"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { formatTimestampDate } from "../../../utils/date"
import { ColumnSortButton, ColumnSortIcon, RowSelectAllCheckbox, RowSelectCheckbox } from "../components"

interface FormTableColumnsConfig {
	formSubmissions: Stored<FormSubmission>[]
}

export const useFormTableColumns = (config: FormTableColumnsConfig) => {
	const { formSubmissions } = config

	const formSubmissionsByForm = useMemo(() => {
		const ret: Record<Form["uuid"], Stored<FormSubmission>[]> = {}

		for (const formSubmission of formSubmissions) {
			const { form } = formSubmission

			if (!form) continue

			if (!(form in ret)) ret[form] = []

			ret[form]!.push(formSubmission)
		}

		return ret
	}, [formSubmissions])

	return useMemo<ColumnDef<Stored<Form>>[]>(
		() => [
			{
				id: "select",
				enableHiding: false,
				header({ table }) {
					return (
						<RowSelectAllCheckbox
							toggleAllRowsSelected={table.toggleAllRowsSelected}
							isSomeRowsSelected={table.getIsSomeRowsSelected()}
							isAllRowsSelected={table.getIsAllRowsSelected()}
						/>
					)
				},
				cell({ row }) {
					return (
						<RowSelectCheckbox isRowSelected={row.getIsSelected()} toggleRowSelected={row.toggleSelected} />
					)
				},
			},
			{
				id: "name",
				accessorFn: (form) => form.name,
				meta: {
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text>{getValue<string>()}</Text>
				},
			},
			{
				id: "submitted_at",
				sortingFn: "datetime",
				accessorFn: (originalRow) => originalRow.submitted_at,
				meta: {
					plainHeader: "Submitted at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Submitted at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
			{
				id: "submissions",
				accessorFn: (form) => {
					return formSubmissionsByForm[form.uuid]?.length ?? 0
				},
				meta: {
					plainHeader: "Submissions",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{getValue<string>()}</Text>
				},
			},
			{
				id: "more",
				enableHiding: false,
				header: "",
				cell: function Cell({ row }) {
					const navigate = useNavigate()

					const handleView = useCallback(() => {
						void navigate({
							from: "/$organizationId/forms/",
							to: "/$organizationId/forms/$formId",
							params: (prev) => ({
								...prev,
								formId: row.original.uuid,
							}),
						})
					}, [navigate, row.original.uuid])

					const handleEdit = useCallback(() => {
						void navigate({
							from: "/$organizationId/forms/",
							to: "/$organizationId/settings/forms/$formId",
							params: (prev) => ({
								...prev,
								formId: row.original.uuid,
							}),
						})
					}, [navigate, row.original.uuid])

					const handleNewEntry = useCallback(() => {
						void navigate({
							from: "/$organizationId/forms/$formId/",
							to: "/$organizationId/forms/$formId/submit",
							params: (prev) => ({
								...prev,
								formId: row.original.uuid,
							}),
						})
					}, [navigate, row.original.uuid])

					return (
						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton variant="ghost" size="sm" accentColor="base" onClick={stopPropagation}>
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onClick={handleView}>
									<LuIcon icon={modelIconMapping.expand} />
									View
								</Menu.Item>
								<Menu.Item onClick={handleNewEntry}>
									<LuIcon icon={modelIconMapping.add} />
									Entry
								</Menu.Item>
								<Menu.Item onClick={handleEdit}>
									<LuIcon icon={modelIconMapping.edit} />
									Edit
								</Menu.Item>
							</Menu.Content>
						</Menu.Root>
					)
				},
			},
		],
		[formSubmissionsByForm],
	)
}

export const useFormTable = (args: Omit<TableOptions<Stored<Form>>, "getCoreRowModel">) => {
	return useReactTable<Stored<Form>>({
		...args,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
